import { addDays, endOfMonth, endOfWeek, format, isValid, parseISO, startOfDay, startOfMonth, startOfWeek } from 'date-fns';

// Regras da Lista do dia (Agenda no celular), sem React e sem relógio: "agora" e "hoje" entram como parâmetro.
// Datas no fuso do navegador (date-fns), como o calendário e o modal do agendamento

// Mesmo corte do menu de celular (AsideBarStyle.css) e da legenda escondida (style.css): os três aparecem juntos
export const PHONE_QUERY = '(max-width: 992px)';
// 'list' é a opção "Horários" (o valor gravado continua o de quando ela se chamava Lista)
export const PHONE_VIEWS = ['list', 'calendar'];
export const PHONE_VIEW_KEY = 'agenda.phone_view';
export const LIST_VIEW_LABEL = 'Horários';
// Abas de Horários: Diário (a Lista do dia) e Semana, lembradas no aparelho
export const LIST_MODES = ['day', 'week'];
export const LIST_MODE_KEY = 'agenda.list_mode';
// Legenda de status do calendário (computador e Calendário do celular): oculta por padrão; o botão Mostrar/Ocultar legenda
// (no celular, no menu ⋮) fica lembrado no aparelho
export const LEGEND_STATES = ['hidden', 'shown'];
export const LEGEND_KEY = 'agenda.legend';
// Totalizadores (cartões de status com a quantidade do mês, no topo do calendário), no computador e no Calendário do celular:
// ocultos por padrão; o botão Mostrar/Ocultar totalizadores (no celular, no menu ⋮) fica lembrado no aparelho
export const TOTALS_STATES = ['hidden', 'shown'];
export const TOTALS_KEY = 'agenda.totals';

const WEEKDAYS = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
export const WEEKDAYS_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
export const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

export const dayKey = (date) => format(date, 'yyyy-MM-dd');
export const monthKey = (date) => format(date, 'yyyy-MM');
export const isSameDay = (a, b) => dayKey(a) === dayKey(b);

// Valor da data nativa ('yyyy-MM-dd') no fuso do navegador: new Date('2026-10-07') cairia no dia 06 em UTC-3.
// null para valor incompleto ou inválido e para ano fora de 1900–2999 (o campo emite 0002, 0020 e 0202 enquanto o ano é digitado)
export function parseDayKey(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value ?? '')) return null;
  const date = parseISO(value);
  if (!isValid(date) || dayKey(date) !== value || date.getFullYear() < 1900 || date.getFullYear() > 2999) return null;
  return date;
}

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);
const yearSuffix = (date, today) => (date.getFullYear() !== today.getFullYear() ? ` de ${date.getFullYear()}` : '');

// "Quarta-feira, 7 de outubro"; o ano aparece quando não é o de hoje
export const dayTitle = (date, today) => capitalize(`${WEEKDAYS[date.getDay()]}, ${date.getDate()} de ${MONTHS[date.getMonth()]}${yearSuffix(date, today)}`);

// "novembro" ("novembro de 2027" fora do ano de hoje), para o aviso de erro do mês
export const monthTitle = (date, today) => `${MONTHS[date.getMonth()]}${yearSuffix(date, today)}`;

// "quarta-feira, 7 de outubro", para os dias da faixa da semana
export const weekDayLabel = (date) => `${WEEKDAYS[date.getDay()]}, ${date.getDate()} de ${MONTHS[date.getMonth()]}`;

// Domingo a sábado da semana do dia (pode cruzar o mês e o ano)
export function weekOf(date) {
  const start = startOfWeek(date);
  return Array.from({ length: 7 }, (_, index) => addDays(start, index));
}

// Dia em que a Lista abre ao voltar do Calendário: "Dia" e "Semana" mantêm o dia; "Mês" e "Agenda do mês" vão para hoje,
// se for o mês de hoje (ano incluído), ou para o dia 1. listDay: o dia em que a Lista estava ao sair; se o calendário
// ficou em "Mês" sem navegar, volta para ele
export function listEntryDay(view, calendarDate, today, listDay = null) {
  if (view === 'day' || view === 'work_week' || view === 'week') return startOfDay(calendarDate);
  if (view === 'month' && listDay && isSameDay(calendarDate, listDay)) return startOfDay(listDay);
  return monthKey(calendarDate) === monthKey(today) ? startOfDay(today) : startOfMonth(calendarDate);
}

// --------------------------------------------------------------------------------------------------------------
// Carregamento do mês

// Chave do carregamento: mês + filtros do pedido. A Lista só mostra cartões (ou "Nenhum agendamento") com a resposta do
// pedido desta chave, nunca com dados de outro mês ou de outro filtro
export function loadKey(date, filters) {
  return [monthKey(date), filters.tecnico_id, filters.paciente_id, filters.tipo_atendimento_id, filters.status_agendamento].map((value) => value ?? '').join('|');
}

export const keyMonth = (key) => (key ? key.split('|')[0] : null);

// Período pedido para o mês: as semanas inteiras das pontas, do domingo da semana do dia 1 ao sábado da semana do último
// dia. A Semana e o calendário (Semana e Mês) mostram dias de outro mês; com só o mês, esses dias ficavam vazios
export function loadRange(date) {
  return {
    data_agendamento_inicial: format(startOfWeek(startOfMonth(date)), 'yyyy-MM-dd'),
    data_agendamento_final: format(endOfWeek(endOfMonth(date)), 'yyyy-MM-dd'),
  };
}

// Cartões de status e total do Calendário só com os agendamentos do mês (o pedido traz também as pontas). Nome, cor, ícone
// e sequência vêm do analytics da resposta, que só tem os status com "Mostrar totalizador" (a regra do backend); a
// contagem é refeita aqui. Mesma ordem do backend: pela sequência e, no empate, pela ordem da lista
export function monthSummary(items, date, analytics) {
  const month = monthKey(date);
  const counts = new Map();
  let total = 0;
  for (const item of items) {
    if (!isValid(item.start) || monthKey(item.start) !== month) continue;
    total += 1;
    const name = item.status?.nome;
    if (name && analytics?.[name]) counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  const cards = [...counts].map(([status, quantidade]) => ({ status, ...analytics[status], quantidade })).sort((a, b) => a.sequencia - b.sequencia);
  return { cards, total };
}

// Doutor escolhido nos chips da Lista, na URL (?tecnico_id=, o mesmo nome do filtro do Dashboard). 'all' = Todos
export const CHIP_PARAM = 'tecnico_id';

export const chipFromSearch = (search) => new URLSearchParams(search ?? '').get(CHIP_PARAM) || 'all';

// Query da URL com o doutor do chip; 'all' tira o parâmetro e mantém os outros. Devolve '' ou '?...'
export function searchWithChip(search, chip) {
  const query = new URLSearchParams(search ?? '');
  if (chip && chip !== 'all') query.set(CHIP_PARAM, chip);
  else query.delete(CHIP_PARAM);
  const text = query.toString();
  return text ? `?${text}` : '';
}

// Filtros da busca do mês (os mesmos nomes do Agendamentos.listAll): vazios = todos
export const NO_FILTERS = { tecnico_id: '', paciente_id: '', tipo_atendimento_id: '', status_agendamento: '' };

// Filtros lidos dos campos do painel Filtros (form da página)
export function filtersFromForm(values) {
  return {
    tecnico_id: values.tecnico_id_filtro ?? '',
    paciente_id: values.paciente_id_filtro ?? '',
    tipo_atendimento_id: values.tipo_atendimento_id_filtro ?? '',
    status_agendamento: values.status_filtro ?? '',
  };
}

// key: chave da resposta na tela; pending: chave pedida que ainda não chegou (esqueleto); error: chave cujo pedido falhou;
// refreshing: atualização da mesma chave (o ícone de Atualizar gira); refreshError: a última atualização falhou (faixa)
export const LOAD_IDLE = { key: null, pending: null, error: null, refreshing: false, refreshError: false };

const sameLoad = (a, b) => a.key === b.key && a.pending === b.pending && a.error === b.error && a.refreshing === b.refreshing && a.refreshError === b.refreshError;

// As três devolvem o mesmo objeto quando nada muda: o computador não ganha render a mais.
// Pedido novo: com a chave da tela é atualização (os cartões ficam; o ícone gira só no modo Lista); com outra chave, esqueleto.
// retry: nova tentativa automática (30 s) do mês que deu erro; o aviso e o "Tentar de novo" ficam até a resposta
export function loadRequested(state, key, refresh, list, retry = false) {
  if (!refresh && retry && state.error === key) return state;
  const next = refresh
    ? { ...state, pending: null, error: null, refreshing: list || state.refreshing }
    : { ...state, pending: key, error: null, refreshing: false };
  return sameLoad(state, next) ? state : next;
}

export function loadSucceeded(state, key) {
  const next = { key, pending: null, error: null, refreshing: false, refreshError: false };
  return sameLoad(state, next) ? state : next;
}

// Atualização que falhou: os cartões ficam e aparece a faixa. Mês ou filtro novo que falhou: o aviso de erro no lugar da lista
export function loadFailed(state, key, refresh) {
  const next = refresh
    ? { ...state, pending: null, refreshing: false, refreshError: true }
    : { ...state, pending: null, refreshing: false, error: key };
  return sameLoad(state, next) ? state : next;
}

// --------------------------------------------------------------------------------------------------------------
// Agendamentos do dia

// Significado dos status, pelo nome sem acento e em maiúsculas: os mesmos nomes e a mesma regra do "Agendados hoje" do
// Dashboard (Ajust-Backend, modules/dashboard/dashboard.rules.ts: STATUS_GROUPS e ReleasedStatusIds), para o número de
// hoje ser igual nos dois lugares. Renomear um status tira ele do grupo nos dois
const BLOCK_STATUS = ['NAO AGENDAR', 'REUNIAO'];
const DONE_OR_NO_SHOW_STATUS = ['REALIZADO', 'AVALIACAO', 'FALTA NAO JUSTIFICADA'];

export const normalizeName = (value) => (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().replace(/\s+/g, ' ').trim();

// Status com "Libera o horário" que não são de realizado nem de falta: o agendamento não ocupa a agenda e conta à parte
export function releasedStatusIds(statusList) {
  return new Set((statusList ?? [])
    .filter((status) => status.releases_slot && !DONE_OR_NO_SHOW_STATUS.includes(normalizeName(status.nome)))
    .map((status) => status.id));
}

// 'appointment' | 'block' (NÃO AGENDAR e REUNIÃO) | 'released' (status que libera o horário)
export function itemKind(item, releasedIds) {
  if (item.status?.id && releasedIds?.has(item.status.id)) return 'released';
  if (BLOCK_STATUS.includes(normalizeName(item.status?.nome))) return 'block';
  return 'appointment';
}

const byText = (a, b) => (a ?? '').localeCompare(b ?? '', 'pt-BR');

// Início, nome do doutor, paciente e id: no mesmo horário cada doutor tem o seu cartão, sempre na mesma ordem
// (a API não ordena, e o orderMap da página tem nomes fixos no código)
export function compareItems(a, b) {
  return (a.start - b.start) || byText(a.tecnico?.nome, b.tecnico?.nome) || byText(a.paciente?.nome, b.paciente?.nome) || byText(String(a.id), String(b.id));
}

// Agendamentos do mês por dia do início ('yyyy-MM-dd'), cada dia já em ordem
export function groupByDay(items) {
  const days = new Map();
  for (const item of items) {
    if (!isValid(item.start)) continue;
    const key = dayKey(item.start);
    if (!days.has(key)) days.set(key, []);
    days.get(key).push(item);
  }
  for (const list of days.values()) list.sort(compareItems);
  return days;
}

// Doutores com agendamento no mês carregado, pelo nome (os chips)
export function monthDoctors(items) {
  const doctors = new Map();
  for (const item of items) {
    if (item.tecnico?.id && !doctors.has(item.tecnico.id)) doctors.set(item.tecnico.id, { id: item.tecnico.id, nome: item.tecnico.nome ?? '' });
  }
  return [...doctors.values()].sort((a, b) => byText(a.nome, b.nome));
}

const TITLES = ['DR', 'DRA', 'DOUTOR', 'DOUTORA'];

function nameWords(nome) {
  const words = (nome ?? '').trim().split(/\s+/).filter(Boolean);
  while (words.length > 1 && TITLES.includes(normalizeName(words[0]).replace(/\.$/, ''))) words.shift();
  return words;
}

// Nome curto dos doutores (id -> nome): o primeiro nome, ou o primeiro e o último se o primeiro se repetir. "DR."/"DRA." não contam
export function shortNames(tecnicos) {
  const words = new Map(tecnicos.map((tecnico) => [tecnico.id, nameWords(tecnico.nome)]));
  const firstCount = new Map();
  for (const list of words.values()) firstCount.set(list[0] ?? '', (firstCount.get(list[0] ?? '') ?? 0) + 1);

  const names = new Map();
  for (const tecnico of tecnicos) {
    const list = words.get(tecnico.id);
    const first = list[0] ?? '';
    names.set(tecnico.id, firstCount.get(first) > 1 && list.length > 1 ? `${first} ${list[list.length - 1]}` : first);
  }
  return names;
}

// Contagem do dia: agendamentos, bloqueios e horários liberados à parte (a regra do "Agendados hoje" do Dashboard)
export function countItems(items, releasedIds) {
  const counts = { appointments: 0, blocks: 0, released: 0 };
  for (const item of items) {
    const kind = itemKind(item, releasedIds);
    if (kind === 'block') counts.blocks += 1;
    else if (kind === 'released') counts.released += 1;
    else counts.appointments += 1;
  }
  return counts;
}

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// "17 agendamentos · 1 bloqueio"; "Nenhum agendamento" quando só há bloqueios (ou nada)
export function countLabel(counts) {
  const parts = [counts.appointments ? plural(counts.appointments, 'agendamento', 'agendamentos') : 'Nenhum agendamento'];
  if (counts.blocks) parts.push(plural(counts.blocks, 'bloqueio', 'bloqueios'));
  if (counts.released) parts.push(`${counts.released} com horário liberado`);
  return parts.join(' · ');
}

// Posição da linha "agora": antes do primeiro item que começa a partir deste minuto. Vale o início: um bloqueio das 13:30 às
// 18:00 não puxa a linha para cima dos cartões da tarde que já passaram. items.length = depois do último
export function nowLineIndex(items, now) {
  const minute = new Date(now);
  minute.setSeconds(0, 0);
  const index = items.findIndex((item) => item.start >= minute);
  return index === -1 ? items.length : index;
}

// "45 min", "1h", "4h30"
export function durationLabel(start, end) {
  const minutes = Math.round((end - start) / 60000);
  if (!(minutes > 0)) return '';
  if (minutes < 60) return `${minutes} min`;
  const rest = minutes % 60;
  return `${Math.floor(minutes / 60)}h${rest ? String(rest).padStart(2, '0') : ''}`;
}

// Tipo de atendimento em letra de frase ("Avaliação sem exames"), para não competir com o nome do paciente
export function sentenceCase(text) {
  const lower = (text ?? '').toLocaleLowerCase('pt-BR');
  return lower.charAt(0).toLocaleUpperCase('pt-BR') + lower.slice(1);
}
